import { Active, CollisionDetection, DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, rectIntersection, useSensor, useSensors } from "@dnd-kit/core";
import { ReactNode, useState } from "react";
import { useAppDispatch } from "../../../store";
import { playlistActions } from "../../playlist/slice";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { ResourceTypeIcon } from "./ResourceTypeIcon";
import { useAsyncRender } from '../../renderer/hooks';
import { uiActions } from '../slice';
import { outputActions } from '../../output/slice';
import { NewResource } from "../../resources/types";

type Props = {
  children?: ReactNode
}

const fixCursorSnapOffset: CollisionDetection = (args) => {
  // Bail out if keyboard activated
  if (!args.pointerCoordinates) {
    return rectIntersection(args);
  }
  const { x, y } = args.pointerCoordinates;
  const { width, height } = args.collisionRect;
  const updated = {
    ...args,
    // The collision rectangle is broken when using snapCenterToCursor. Reset
    // the collision rectangle based on pointer location and overlay size.
    collisionRect: {
      width,
      height,
      bottom: y + height / 2,
      left: x - width / 2,
      right: x + width / 2,
      top: y - height / 2,
    },
  };
  return rectIntersection(updated);
};

export const AppDragContext = ({ children }: Props) => {

  const [activeDragItem, setActiveDragItem] = useState<Active>();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 2,
      },
    })
  );

  const dispatch = useAppDispatch();

  const render = useAsyncRender();

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragItem(event.active);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragItem(undefined);

    const isPlaylistDrop = (event.over?.id === 'playlist' || event.over?.data?.current?.type === 'playlistEntry');
    const isResourceDrag = event.active.data.current?.type === 'resource';
    const isPlaylistDrag = event.active.data.current?.type === 'playlistEntry';

    if (isPlaylistDrop) {
      if (isResourceDrag) {
        const resource = event.active.data.current!.resource as NewResource;

        const index = event.over?.data?.current?.index === undefined ? undefined : event.over.data.current.index;

        if (resource.id) {
          dispatch(
            playlistActions.addItem({
              resourceID: resource.id,
              title: resource.title,
              type: resource.type,
              index
            })
          );
        }
        else {
          dispatch(
            playlistActions.addItem({ index, resource: event.active.data.current!.resource })
          );
        }
      }
      else if (isPlaylistDrag) {
        const fromIndex = event.active.data.current!.index;
        const toIndex = event.over?.data?.current?.index === undefined ? undefined : event.over.data.current.index;
        dispatch(playlistActions.moveItem({ fromIndex, toIndex }));
      }
    }
    else if (event.over?.id === 'live' && event.active.data.current?.resource) {
      render(event.active.data.current?.resource).then(
        (rendered) => {
          if (rendered) {
            dispatch(uiActions.setCurrentLiveResource({ resource: rendered.resource }));
            dispatch(outputActions.setBlockAddress({ block: 0, group: 0, line: 0 }));
          }
        }
      );
    }
  };

  return (
    <DndContext
      collisionDetection={fixCursorSnapOffset}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      { children }
      <DragOverlay
        dropAnimation={null}
        modifiers={[snapCenterToCursor]}
        style={{
          width: '300px'
        }}
      >
        {
          activeDragItem && activeDragItem.data.current && (
          <div className='flex align-middle bg-white border border-jw-blue z-50 cursor-grab font-bold p-2 rounded-sm'>
            <ResourceTypeIcon
              className='size-8'
              resourceType={activeDragItem.data.current.resource.type}
            />
            { activeDragItem.data.current.resource.title }
          </div>
          )
        }
      </DragOverlay>

    </DndContext>
  );
};
